import { DataEditContextSkillField } from 'Components/reusable/DataContext/fields/DataEditContextSkillField'
import { DataEditContextCertificateField } from 'Components/reusable/DataContext/fields/DataEditContextCertificateField'
import { DataEditContextCompetenceField } from 'Components/reusable/DataContext/fields/DataEditContextCompetenceField'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ISearchCertificate, ISearchSkill, ISearchCompetence } from 'types/searchInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import * as Yup from 'yup'
import DataContext from 'Components/reusable/DataContext'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { ICertificate, ISkillOrIndustryOrRole, ICompetence } from 'types/cvsInterfaces'
import { useUser } from 'hooks'

/** @notExported */
interface ISkillSearchRequirementProps {
  /** Function to add requirement. */
  onAdd: (newItem?: ISearchSkill | ISearchCertificate | ISearchCompetence) => void
  /** Type of skill. */
  type: 'skill' | 'industry' | 'role' | 'all' | 'certificate' | 'competence'
  /** Whether to show label. */
  noLabel?: boolean
  /** Whether to show unpromoted skills. */
  showUnpromoted?: boolean
  /** The skills that can be selected. */
  options?: ISkillOrIndustryOrRole[] | ICertificate[] | ICompetence[]
  /** Whether to show inset label. */
  insetLabel?: boolean
}

/**
 * Search requirement field for skills or roles or industries.
 *
 * @returns Skill search requirement component.
 * @notExported
 */
const SkillSearchRequirement: React.FC<ISkillSearchRequirementProps> = ({
  onAdd,
  type,
  noLabel,
  showUnpromoted = false,
  options,
  insetLabel,
}) => {
  const [data, setData] = useState<ISearchSkill | ISearchCertificate | ISearchCompetence | null>(null)
  const { i18n, t } = useTranslation()
  const { features } = useUser()

  const skillSchema =
    type === 'all'
      ? Yup.object({
          skillId: Yup.number().required(i18n.t('fieldRequired')),
        })
      : Yup.object({
          skillId: Yup.number().required(i18n.t('fieldRequired')),
          experienceMonths: Yup.number().typeError(i18n.t('numberTypeError')).required(i18n.t('fieldRequired')),
        })

  const certificateSchema = Yup.object({
    certificateId: Yup.number().required(i18n.t('fieldRequired')),
  })

  const competenceSchema = Yup.object({
    competenceId: Yup.number().required(i18n.t('fieldRequired')),
  })

  useEffect(() => {
    if (type !== 'certificate' && type !== 'competence') {
      setData({
        name: '',
        experienceMonths: 0,
        level: 0,
        required: true,
        skillId: null,
        Skill: null,
      } as unknown as ISearchSkill)
    } else if (type === 'certificate') {
      setData({
        certificate: '',
        required: true,
        certificateId: null,
        Certificate: null,
      } as unknown as ISearchCertificate)
    } else if (type === 'competence' && features.includes('competences')) {
      setData({
        name: '',
        required: true,
        competenceId: null,
        Competence: null,
      } as unknown as ISearchCompetence)
    }
  }, [features, onAdd, type])

  if (!data) {
    return <LoadingIndicator />
  }

  const saveData = (newData: ISearchSkill | ISearchCertificate | ISearchCompetence) => {
    setData(newData)
    if ('skillId' in newData && newData.skillId) {
      const skillTranslations = newData.Skill && chooseDBTranslation(i18n, newData.Skill)
      onAdd({ ...newData, name: skillTranslations?.name ?? '', required: true })
    }
    if ('certificateId' in newData && newData.certificateId) {
      const certificateTranslations = newData.Certificate && chooseDBTranslation(i18n, newData.Certificate)
      onAdd({ ...newData, certificate: certificateTranslations?.certificate ?? '', required: true })
    }
    if ('competenceId' in newData && newData.competenceId && features.includes('competences')) {
      const competenceTranslations = newData.Competence && chooseDBTranslation(i18n, newData.Competence)
      onAdd({ ...newData, name: competenceTranslations?.name ?? '', required: true })
    }
  }

  return (
    <DataContext
      data={data}
      localeBase={`resources.searchEmployees.${type}`}
      schema={
        type !== 'certificate' && type !== 'competence'
          ? skillSchema
          : type === 'certificate'
            ? certificateSchema
            : competenceSchema
      }
      onChange={saveData}
    >
      {type !== 'certificate' && type !== 'competence' ? (
        <DataEditContextSkillField<ISearchSkill>
          field="skillId"
          dataField="Skill"
          noPlaceholders={false}
          placeholder={noLabel ? t('search.allSkills') : undefined}
          hideLabel={noLabel}
          fullWidth
          submitOnEnter={true}
          type={type}
          search
          showUnpromoted={showUnpromoted}
          options={options as ISkillOrIndustryOrRole[] | undefined}
          insetLabel={insetLabel}
          resetOnSelect
        />
      ) : type === 'certificate' ? (
        <DataEditContextCertificateField<ISearchCertificate>
          field="certificateId"
          dataField="Certificate"
          placeholder={noLabel ? t('search.allCertificates') : undefined}
          hideLabel={noLabel}
          fullWidth
          submitOnEnter={true}
          options={options as ICertificate[] | undefined}
          insetLabel={insetLabel}
          resetOnSelect
        />
      ) : type === 'competence' && features.includes('competences') ? (
        <DataEditContextCompetenceField<ISearchCompetence>
          field="competenceId"
          dataField="Competence"
          placeholder={noLabel ? t('search.allCompetences') : undefined}
          hideLabel={noLabel}
          fullWidth
          submitOnEnter={true}
          options={options as ICompetence[] | undefined}
          insetLabel={insetLabel}
          resetOnSelect
        />
      ) : null}
    </DataContext>
  )
}

export default SkillSearchRequirement
